import { type HexColor } from '@myre/common-primitives';
import { colorArray } from '@myre/common-styles';
import { TranslatePipe } from '@myre/ui-primitive-core';

import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';

import { ColorPickerListComponent } from './colorpicker-list/color-picker-list.component';

export const COLORPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ColorPickerComponent),
  multi: true,
};

@Component({
  selector: 'myre-colorpicker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  providers: [COLORPICKER_VALUE_ACCESSOR],
  imports: [NgClass, ColorPickerListComponent, FormsModule, TranslatePipe],
})
export class ColorPickerComponent implements ControlValueAccessor {
  private _elementRef = inject(ElementRef);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  @Input()
  public verticalDirection: 'up' | 'down' = 'down';

  @Input()
  public horizontalDirection: 'left' | 'right' = 'right';

  @Input()
  public activeColors: string[] = [];

  @Input()
  public disabled = false;

  @Input()
  public readonly = false;

  public colors = colorArray;
  public value: HexColor;
  public isOpen: boolean;
  public selectedColorSet: Record<string, HexColor>;

  @HostListener('document:click.out-zone', ['$event.target'])
  public clickOutside(target: EventTarget): void {
    if (
      this.isOpen &&
      target instanceof Node &&
      !this._elementRef.nativeElement.contains(target)
    ) {
      this.toggleMenu();
      this._changeDetectorRef.detectChanges();
    }
  }

  // TODO: Make accessible with keyup events

  public writeValue(value: HexColor): void {
    this.value = value;
    this.selectedColorSet =
      this.colors.find(colorSet =>
        Object.values(colorSet).includes(this.value),
      ) || this.colors[0];
  }

  public registerOnChange(fn: (value: string) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public toggleMenu(): void {
    if (!this.disabled && !this.readonly) {
      if (this.isOpen) {
        this._onTouched();
      }

      this.isOpen = !this.isOpen;
    }
  }

  public onChange(color: string): void {
    if (color[0] !== '#') {
      color = '#' + color;
    }

    this.writeValue(color as HexColor);
    this._onModelChange(color);
  }

  private _onModelChange: (value: string) => any = () => {};

  private _onTouched: () => any = () => {};
}
